import { wizard, cards } from '../../modal/wizard';

/**
 * What the join modal's stages hold, on top of the shared wizard.
 *
 * Stages 1 and 2 are the forum's own: the organization card decides which
 * requirements copy shows and which value lands in #user_organization_type. Stage 3
 * is the registration form, whose submit belongs to guest-steps.js (a guest) or
 * member.js (a member). This file must not touch #registerButton's handler, only
 * its visibility.
 *
 * Opened from the Forum page the modal draws one stage before those: the chapter
 * itself, which then has to reach the form the page could not address.
 */

const MODAL_ID = 'lkJoinModal';

export function joinWizard() {
    const modal = document.getElementById(MODAL_ID);

    if (! modal) {
        return;
    }

    const stages = wizard(modal);

    const body        = modal.querySelector('.lk-modal-body');
    const subtypeWrap = modal.querySelector('#lkJoinSubtypes');
    const ackWrap     = modal.querySelector('#lkJoinAck');
    const ackBox      = modal.querySelector('#lkJoinAckBox');
    const selectedLbl = modal.querySelector('#lkJoinSelectedLabel');
    const continueOrg = modal.querySelector('#lkJoinContinueOrg');
    const messages    = window.lkJoinMessages || {};

    const state = { chapter: '', chapterName: '', key: '', path: '', label: '', subtype: '', subtypeLabel: '' };

    // ── Stage 0: which chapter ───────────────────────────────────────────
    // Drawn only by the Forum page, which opens the door without one. A chapter
    // or event page already knows it and this stage does not exist.

    const chapterField    = modal.querySelector('#lkJoinChapter');
    const continueChapter = modal.querySelector('#lkJoinContinueChapter');

    if (chapterField) {
        const chip  = modal.querySelector('#lkJoinChapterChip');
        const title = modal.querySelector('.lk-modal-head-title');
        const names = modal.querySelectorAll('[data-join-chapter-name]');

        chapterField.addEventListener('change', () => {
            const option = chapterField.selectedOptions[0];

            state.chapter     = chapterField.value;
            state.chapterName = option ? option.dataset.chapterName || '' : '';

            names.forEach((element) => { element.textContent = state.chapterName; });

            if (chip) chip.hidden = ! state.chapter;
            if (continueChapter) continueChapter.disabled = ! state.chapter;

            // The heading names the chapter from here on, the way it does on a
            // chapter page, where it is known before the modal is drawn.
            if (title && state.chapter && messages.chapter_title) {
                title.textContent = messages.chapter_title.replace(':chapter', state.chapterName);
            }
        });
    }

    if (continueChapter) {
        continueChapter.addEventListener('click', () => {
            if (! state.chapter) return;

            applyChapter();
            stages.go('org');
        });
    }

    // A guest posts the chapter as a field; a member's request carries it in the
    // path, so the form is drawn with a placeholder in its address.
    function applyChapter() {
        modal.querySelectorAll('input[name="chapter_id"], input[name="chapterId"]').forEach((input) => {
            input.value = state.chapter;
        });

        const memberForm = modal.querySelector('#joinForm[data-chapter-action]');

        if (memberForm) {
            memberForm.action = memberForm.dataset.chapterAction.replace('__chapter__', state.chapter);
        }
    }

    // ── Stage 1: organization type ───────────────────────────────────────

    const cardGroup = modal.querySelector('.lk-wizard__cards');

    if (cardGroup) {
        const organization = cards(cardGroup, (card) => {
            state.key     = card.dataset.orgKey;
            state.path    = card.dataset.orgPath;
            state.label   = card.dataset.orgLabel;
            state.subtype = '';
            state.subtypeLabel = '';

            if (continueOrg) continueOrg.disabled = false;
        });

        // A profile that already carries a type opens on that card.
        const preselected = cardGroup.querySelector('.lk-wizard__card.is-selected');
        if (preselected) organization.choose(preselected);
    }

    if (continueOrg) {
        continueOrg.addEventListener('click', () => {
            if (!state.key) return;
            if (ackBox) ackBox.checked = false;
            modal.querySelectorAll('input[name="lk_join_subtype"]').forEach((r) => { r.checked = false; });
            state.subtypeLabel = '';
            renderRules();
            stages.go('rules');
        });
    }

    // ── Stage 2: requirements ────────────────────────────────────────────

    function renderRules() {
        if (selectedLbl) selectedLbl.textContent = state.label;

        modal.querySelectorAll('.lk-join__rules').forEach((block) => { block.hidden = true; });

        const isGroup = state.key === 'gov_academic_nonprofit';
        if (subtypeWrap) subtypeWrap.hidden = !isGroup;

        const target = isGroup && state.subtype
            ? state.key + ':' + state.subtype
            : state.key;

        const block = modal.querySelector('[data-rules="' + target + '"]');
        if (block) block.hidden = false;

        // The group's shared benefits only make sense once a subtype names
        // which set of rules is on screen.
        const resolved = !isGroup || state.subtype;
        const free     = modal.querySelector('#lkJoinFreeBenefits');
        const paid     = modal.querySelector('#lkJoinPaidAccess');

        if (free) free.hidden = !(resolved && (state.path === 'free' || state.path === 'free-group'));
        if (paid) paid.hidden = state.path !== 'paid';
        if (ackWrap) ackWrap.hidden = state.path !== 'paid';

        // Event door only: a guest seat and a member seat are worth
        // different sentences.
        const note = state.path === 'paid' ? 'paid' : 'free';
        modal.querySelectorAll('[data-event-note]').forEach((el) => {
            el.hidden = el.dataset.eventNote !== note;
        });
    }

    modal.querySelectorAll('input[name="lk_join_subtype"]').forEach((radio) => {
        radio.addEventListener('change', () => {
            state.subtype = radio.value;
            // The value is the key the copy is filed under; what the reader
            // sees is the label beside the box, which is translated.
            state.subtypeLabel = (radio.parentElement.querySelector('span') || {}).textContent || radio.value;
            stages.clearError();
            renderRules();
        });
    });

    const continueRules = modal.querySelector('#lkJoinContinueRules');
    if (continueRules) {
        continueRules.addEventListener('click', () => {
            if (state.key === 'gov_academic_nonprofit' && !state.subtype) {
                stages.showError(subtypeWrap, messages.subtype_required);
                return;
            }
            if (state.path === 'paid' && ackBox && !ackBox.checked) {
                stages.showError(ackWrap, messages.ack_required);
                return;
            }

            applyToForm();
            stages.go('form');
        });
    }

    // ── Stage 3: carry the choice into the form ──────────────────────────

    function applyToForm() {
        const field   = modal.querySelector('#user_organization_type');
        const display = modal.querySelector('#user_organization_type_display');

        if (field) field.value = state.key;
        // The subtype is the more precise word for the reader, even though
        // the group key is what gets stored.
        if (display) display.value = state.subtypeLabel.trim() || state.label;
    }

    // ── Guest verification step ──────────────────────────────────────────
    // core/emailVerification.js hides the form and reveals the code section in
    // its place. While that is on screen there is nothing to go back to, so the
    // stage's footer controls step aside and the modal stops asking to
    // confirm a discard.

    const form = modal.querySelector('#registrationForm');
    let verifying = false;

    if (form) {
        new MutationObserver(() => {
            const nowVerifying = form.style.display === 'none';
            if (nowVerifying !== verifying) {
                verifying = nowVerifying;
                modal.querySelectorAll('[data-wizard-foot="form"]').forEach((el) => {
                    el.hidden = verifying;
                });
                if (verifying && window.LkModal) LkModal.setDirty(MODAL_ID, false);
            }
        }).observe(form, { attributes: true, attributeFilter: ['style'] });

        // Sections appear below the fold as the email check resolves.
        if (body) {
            new MutationObserver(() => {
                body.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
            }).observe(form, { subtree: true, attributes: true, attributeFilter: ['style'] });
        }
    }

    // A verified guest has an account now, and the page has to show it.
    modal.addEventListener('lk:close', () => {
        if (verifying) window.location.reload();
    });
}
